/**
 * manifestIntentTables — manifest-intent keyword/negation tables and their
 * matchers, lifted VERBATIM from the deleted
 * services/globalTools/orchestrator/multiDocManifestPreRoute.ts
 * (D13-6b, 2026-08-25). The deterministic pre-route that consumed them died
 * with the in-browser orchestrator; these tables survive because the LIVE
 * lanes still use them as evidence/detection (awaitingInputBreakOut's
 * mid-clarify manifest handling, legalDocCreator's affirmative-intent
 * check) — matching, never routing.
 */

/**
 * Manifest-intent keywords (English + Spanish). A case-insensitive substring
 * match against the user's message is sufficient. Keep this list conservative
 * — every keyword here short-circuits the LLM decision when the attachment
 * threshold is met, so false positives are costly.
 */
export const MANIFEST_KEYWORDS: string[] = [
  // English — primary phrasings
  'manifest',
  'tagged manifest',
  'tagged list',
  'tagged index',
  'list of files',
  'list what is in',
  "list what's in",
  'list what you read',
  'list what you just read',
  'what are these',
  'what did you just read',
  'what did you read',
  'what are in these',
  'index these',
  'create an index',
  'make an index',
  'show me a manifest',
  'give me a manifest',
  'give me a tagged',
  'give me an index',
  'organize these',
  'organise these',
  'catalog',
  'catalogue',
  'summary of each',
  'summary per file',

  // Spanish equivalents
  'manifiesto',
  'índice',
  'indice',
  'lista de archivos',
  'lista etiquetada',
  'catálogo',
  'catalogo',
  'organiza estos',
  'organícelos',
  'resumen de cada',
  'resumen por archivo',
];

/**
 * Explicit negation patterns for the legal-document-drafting workflow.
 * If the user writes any of these AND uploads multiple files, we treat it
 * as a hard veto on `legal-doc-creator` and fall through to the manifest
 * workflow so the user can see what they uploaded.
 *
 * These patterns are intentionally permissive on whitespace / contractions
 * ("don't" / "don't" / "do not") and cover the common legal document types
 * we generate (pre-hearing statement, legal filing, legal document, PHS,
 * demand letter, I-589).
 */
export const LEGAL_DOC_NEGATION_PATTERNS: RegExp[] = [
  // Generic "do not draft / don't draft / don't create"
  /\bdo\s*n[o']?t\s+(draft|create|generate|prepare|make|write)\b/i,
  /\bdon['’]?t\s+(draft|create|generate|prepare|make|write)\b/i,
  /\bno\s+drafting\b/i,
  /\bwithout\s+drafting\b/i,

  // "not a pre-hearing statement" / "not a legal filing" / "not a legal doc"
  /\bnot\s+a\s+pre[\s-]?hearing\s+statement\b/i,
  /\bnot\s+a\s+(legal|court)\s+(filing|document|pleading|statement)\b/i,
  /\bnot\s+a\s+phs\b/i,
  /\bnot\s+a\s+demand\s+letter\b/i,
  /\bnot\s+an?\s+i[\s-]?589\b/i,

  // "skip the pre-hearing / skip the filing"
  /\bskip\s+(the\s+)?(pre[\s-]?hearing|legal\s+filing|phs|filing|drafting)\b/i,

  // Spanish negations
  /\bno\s+redactes?\b/i,
  /\bsin\s+redactar\b/i,
  /\bno\s+(prepares?|generes?)\b/i,
];

export const AFFIRMATIVE_LEGAL_DOC_DRAFT_PATTERNS: RegExp[] = [
  /\b(create|draft|prepare|generate|write|make)\s+(a\s+|an\s+)?(legal\s+)?(pre[-\s]?hearing\s+statement|prehearing\s+statement|phs|statement|filing|pleading|demand\s+letter|legal\s+document)\b/i,
  /\b(create|draft|prepare|generate|write|make)\b[\s\S]{0,80}\b(pre[-\s]?hearing\s+statement|prehearing\s+statement|phs|demand\s+letter)\b/i,
  /\bplease\s+(create|draft|prepare|generate|write|make)\b/i,
  /\busing\s+(these|the\s+following)\s+(documents|files|information)\b/i,
  /\buse\s+the\s+word\s+format\b/i,
  /\buse\s+the\s+sample\b/i,
  /\bprovided\s+to\s+the\s+(eoir|immigration)\s+court\b/i,
  /\bcrear|redactar|preparar|generar\b[\s\S]{0,80}\b(declaraci[oó]n\s+previa|documento|carta\s+de\s+demanda)\b/i,
];

export function findAffirmativeLegalDocDraftIntent(message: string): RegExp | undefined {
  if (!message) return undefined;
  for (const rx of AFFIRMATIVE_LEGAL_DOC_DRAFT_PATTERNS) {
    if (rx.test(message)) return rx;
  }
  return undefined;
}

/**
 * Minimum attachment count required to trigger the keyword-based
 * deterministic pre-route (Condition A). The bug report describes the
 * 20-file case but 10 is a safer lower bound that still avoids regressions
 * on smaller multi-doc flows where the LLM handles routing well.
 */
export const MANIFEST_PREROUTE_MIN_FILES = 10;

/**
 * Minimum attachment count required to trigger the negation-based
 * deterministic pre-route (Condition B). We use a lower threshold (2)
 * here because "do NOT draft X" is an unambiguously explicit veto —
 * a single-file case would likely want `document-analyzer` instead, but
 * any case with 2+ files and an explicit legal-doc negation is almost
 * always a manifest request.
 */
export const NEGATION_MANIFEST_MIN_FILES = 2;

/**
 * Case-insensitive substring scan for manifest-intent keywords.
 * Returns the first matched keyword, or undefined if none matched.
 */
export function findManifestKeyword(message: string): string | undefined {
  if (!message) return undefined;
  const n = message.toLowerCase();
  for (const kw of MANIFEST_KEYWORDS) {
    if (n.includes(kw.toLowerCase())) return kw;
  }
  return undefined;
}

/**
 * Scan for an explicit legal-document-drafting negation.
 * Returns the first matching pattern, or undefined if none matched.
 */
export function findLegalDocNegation(message: string): RegExp | undefined {
  if (!message) return undefined;
  for (const rx of LEGAL_DOC_NEGATION_PATTERNS) {
    if (rx.test(message)) return rx;
  }
  return undefined;
}
